import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Space, message, Divider } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined } from '@ant-design/icons';

type LoginType = 'password' | 'sms';

export type LoginValues = {
  type: LoginType;
  username?: string;
  password?: string;
  phone?: string;
  smsCode?: string;
};

interface LoginFormProps {
  form: any;
  loading: boolean;
  onFinish: (values: LoginValues) => void;
  disabled?: boolean; // 新增disabled属性
  sendSmsCodeApi?: (phone: string, type: string) => Promise<any>;
}

const phoneRegex = /^1[3-9]\d{9}$/;

const LoginForm: React.FC<LoginFormProps> = ({ form, loading, onFinish, sendSmsCodeApi, disabled = false }) => {
  const [loginType, setLoginType] = useState<'password' | 'sms'>('sms');
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setIsPhoneValid(phoneRegex.test(phone));
  };

  const handleSendSmsCode = async () => {
    try {
      const phone = form.getFieldValue('phone');
      if (!phone) {
        message.error('请先输入手机号码');
        return;
      }
      if (!phoneRegex.test(phone)) {
        message.error('请输入有效的手机号码');
        return;
      }
      setSendingCode(true);
      const response = await sendSmsCodeApi(phone, 'login');
      if (!response.code) {
        message.success('验证码已发送');
        setCountdown(60);
        timerRef.current = setInterval(() => {
          setCountdown(prevCountdown => {
            if (prevCountdown <= 1) {
              clearInterval(timerRef.current as NodeJS.Timeout);
              timerRef.current = null;
              return 0;
            }
            return prevCountdown - 1;
          });
        }, 1000);
      } else {
        message.error('发送验证码失败：' + response.message);
      }
    } catch (error) {
      console.error('发送验证码错误:', error);
      message.error(error.message || '发送验证码过程中发生错误，请稍后再试');
    } finally {
      setSendingCode(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="login-form-wrapper">
      {/* 登录方式切换 */}
      <div className="login-type-switcher">
        <button
          type="button"
          className={`type-button ${loginType === 'sms' ? 'active' : ''}`}
          onClick={() => {
            setLoginType('sms');
            form.resetFields();
          }}
          disabled={disabled}
        >
          验证码登录
        </button>
        <button
          type="button"
          className={`type-button ${loginType === 'password' ? 'active' : ''}`}
          onClick={() => {
            setLoginType('password');
            form.resetFields();
          }}
          disabled={disabled}
        >
          密码登录
        </button>
      </div>

      <Form
        form={form}
        name="login"
        onFinish={(values) => onFinish({ ...values, type: loginType})}
        autoComplete="off"
        size="large"
        className="login-form"
        disabled={disabled}
      >
        {loginType === 'password' ? (
          <>
            <Form.Item
              name="username"
              rules={[{ required: true, message: '请输入用户名!' }]}
            >
              <Input 
                prefix={<UserOutlined className="input-icon" />} 
                placeholder="用户名" 
                className="form-input"
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: '请输入密码!' }]}
            >
              <Input.Password 
                prefix={<LockOutlined className="input-icon" />} 
                placeholder="密码" 
                className="form-input"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="phone"
              rules={[
                { required: true, message: '请输入手机号码!' },
                { pattern: phoneRegex, message: '请输入有效的手机号码!' }
              ]}
            >
              <Input
                prefix={<PhoneOutlined className="input-icon" />}
                placeholder="手机号码"
                onChange={handlePhoneChange}
                className="form-input"
              />
            </Form.Item>
            <Form.Item
              name="smsCode"
              rules={[{ required: true, message: '请输入短信验证码!' }]}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Input 
                  placeholder="短信验证码" 
                  className="form-input sms-input"
                />
                <Button
                  type="primary"
                  onClick={handleSendSmsCode}
                  loading={sendingCode}
                  disabled={countdown > 0 || !isPhoneValid}
                  className="sms-button"
                >
                  {countdown > 0 ? `${countdown}s` : '发送验证码'}
                </Button>
              </Space.Compact>
            </Form.Item>
          </>
        )}
        
        <Form.Item className="login-button-item">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={disabled}
            block
            size="large"
            className={`login-button ${disabled ? 'disabled' : ''}`}
          >
            登录
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default LoginForm;
